import { createContext, useContext, type HTMLProps } from "react";

/** Roles Base UI gives a picker popup that WAI-ARIA requires a name for. */
const NAMED_POPUP_ROLES: ReadonlySet<string> = new Set(["dialog", "listbox"]);

/** The nearest `Picker.Popup`'s `aria-label`; each popup resets it for its own level. */
export const PickerPopupNameContext = createContext<string | undefined>(
    undefined,
);

/**
 * Base UI `render` for a popup named `ariaLabel`. Base UI decides the role
 * (a dialog around a popup search, a listbox with no `Picker.List`, otherwise
 * presentation), and a presentational element must not carry a name.
 */
export const renderNamedPopup =
    (ariaLabel: string) =>
    ({ role, ...props }: HTMLProps<HTMLDivElement>) => (
        <div
            {...props}
            role={role}
            aria-label={
                role !== undefined && NAMED_POPUP_ROLES.has(role)
                    ? ariaLabel
                    : undefined
            }
        />
    );

/**
 * A list's naming attributes: its own `aria-labelledby` wins, then its own
 * `aria-label`, then the popup's `aria-label`; only one is set.
 */
export const usePickerListName = ({
    ariaLabel,
    ariaLabelledBy,
}: {
    ariaLabel: string | undefined;
    ariaLabelledBy: string | undefined;
}) => {
    const popupName = useContext(PickerPopupNameContext);
    if (ariaLabelledBy !== undefined) {
        return { "aria-labelledby": ariaLabelledBy };
    }
    return { "aria-label": ariaLabel ?? popupName };
};
